import React, { useState, useEffect, useCallback, useRef } from 'react'
import {
	getEmailAccounts,
	requestEmailAccess,
	connectSmtpAccount,
	reauthorizeEmailAccount,
	deleteEmailAccount,
	saveAccountSignature,
	deleteAccountSignature,
} from '../../services/httpService'
import { useLocation, useNavigate } from 'react-router-dom'

import ConnectEmailAccountModal from './ConnectEmailAccountModal'
import EmailAccountList from './EmailAccountList'
import EmailTemplates from './EmailTemplates'
import LimitReachedModal from './LimitReachedModal'
import { useDarkMode } from '../../context/DarkModeContext'
import { getCurrentUser } from '../../services/authService'
import SuccessMessageHandler from '../EmailAccessModal/EmailSuccessMessageHandler'

const serverMessage = (error, fallback) => error.response?.data?.message || fallback

const mailboxLimitMessage = allowance => {
	const accountsText = allowance?.limit === 1 ? 'email account' : 'email accounts'
	const limitText = allowance ? `limit of ${allowance.limit} ${accountsText}` : 'email account limit'
	return `You have reached your plan's ${limitText}. Please delete an existing account before adding a new one.`
}

const TABS = [
	{ id: 'accounts', label: 'Email accounts' },
	{ id: 'templates', label: 'Templates' },
]

const ACTION_BUTTON = 'w-full whitespace-nowrap bg-blue-600 hover:bg-blue-700 text-white px-3 py-1.5 rounded text-sm sm:w-auto disabled:opacity-50'

function EmailSettings() {
	const { isDarkMode } = useDarkMode()
	const isLight = !isDarkMode
	const location = useLocation()
	const navigate = useNavigate()
	const activeTab = new URLSearchParams(location.search).get('tab') === 'templates' ? 'templates' : 'accounts'
	const templatesRef = useRef(null)
	const [accounts, setAccounts] = useState([])
	const [mailboxAllowance, setMailboxAllowance] = useState(null)
	const [loading, setLoading] = useState(true)
	const [loadFailed, setLoadFailed] = useState(false)
	const [error, setError] = useState(null)
	const [warning, setWarning] = useState(null)
	const [modalError, setModalError] = useState(null)
	const [submitting, setSubmitting] = useState(false)
	const [showSuccess, setShowSuccess] = useState(false)
	const [isConnectModalOpen, setIsConnectModalOpen] = useState(false)
	const [isMaxAccountsModalOpen, setIsMaxAccountsModalOpen] = useState(false)
	const hideSuccess = useCallback(() => setShowSuccess(false), [])
	const isAtMailboxLimit = Boolean(mailboxAllowance) && mailboxAllowance.inUse >= mailboxAllowance.limit

	const fetchEmailData = async () => {
		try {
			const response = await getEmailAccounts()
			setAccounts(response.data.accounts)
			setMailboxAllowance({ limit: response.data.mailboxLimit, inUse: response.data.mailboxesInUse })
			setLoadFailed(false)
		} catch (err) {
			setLoadFailed(true)
			setError(serverMessage(err, 'Could not load your email accounts.'))
		} finally {
			setLoading(false)
		}
	}

	useEffect(() => {
		fetchEmailData()
	}, [])

	// Gmail and Outlook send the browser back here with ?emailSetup=success,
	// noSend (connected, but send was unticked), error (with a message) or
	// maxReached. Show the result, then drop the query.
	useEffect(() => {
		const params = new URLSearchParams(location.search)
		const emailSetup = params.get('emailSetup')
		if (!emailSetup) return

		if (emailSetup === 'success') setShowSuccess(true)
		if (emailSetup === 'noSend') {
			setWarning("Mailbox connected, but sending wasn't allowed. Click Re-authorize and tick the send permission.")
		}
		if (emailSetup === 'maxReached') setIsMaxAccountsModalOpen(true)
		if (emailSetup === 'error') {
			setError(params.get('message') || 'Connecting the mailbox failed. Please try again.')
		}
		navigate(location.pathname, { replace: true })
	}, [location, navigate])

	// Returns true when the SMTP account was connected, so the modal can reset.
	// `details` is the SMTP config for SMTP, or { readAccess } for Gmail.
	const handleEmailAuthorization = async (provider, details = null) => {
		if (submitting) return false
		if (isAtMailboxLimit) {
			setIsMaxAccountsModalOpen(true)
			return false
		}

		setSubmitting(true)
		setModalError(null)
		try {
			if (provider === 'smtp') {
				await connectSmtpAccount(details)
				await fetchEmailData()
				setIsConnectModalOpen(false)
				setError(null)
				return true
			}
			const response = await requestEmailAccess({ provider, readAccess: details?.readAccess })
			if (response.data.authUrl) window.location.href = response.data.authUrl
			return false
		} catch (err) {
			console.error('Error connecting email account:', err.response?.status || err.message)
			setModalError(serverMessage(err, 'Connecting the mailbox failed. Please try again.'))
			return false
		} finally {
			setSubmitting(false)
		}
	}

	const handleDeleteAccount = async accountId => {
		try {
			await deleteEmailAccount(accountId)
			await fetchEmailData()
		} catch (err) {
			throw new Error(serverMessage(err, 'Failed to remove the account'))
		}
	}

	const handleSaveSignature = async (accountId, signatureData) => {
		try {
			await saveAccountSignature(accountId, signatureData)
		} catch (err) {
			throw new Error(serverMessage(err, 'Saving the signature failed. Please try again.'))
		}
		await fetchEmailData()
	}

	const handleRemoveSignature = async accountId => {
		try {
			await deleteAccountSignature(accountId)
		} catch (err) {
			throw new Error(serverMessage(err, 'Removing the signature failed. Please try again.'))
		}
		await fetchEmailData()
	}

	const switchTab = tab => {
		navigate(tab === 'templates' ? `${location.pathname}?tab=templates` : location.pathname, { replace: true })
	}

	// `readAccess` adds Gmail read access on the way through (the Enable link).
	const handleReAuthorization = async (email, { readAccess = false } = {}) => {
		if (submitting) return
		setSubmitting(true)
		try {
			const response = await reauthorizeEmailAccount({ email, readAccess })
			if (response.data.authUrl) window.location.href = response.data.authUrl
		} catch (err) {
			console.error('Error re-authorizing email account:', err.response?.status || err.message)
			setError(serverMessage(err, 'Re-authorizing failed. Please try again.'))
		} finally {
			setSubmitting(false)
		}
	}

	const handleModalOpen = () => {
		if (isAtMailboxLimit) {
			setIsMaxAccountsModalOpen(true)
			return
		}
		setModalError(null)
		setIsConnectModalOpen(true)
	}

	const accountsLoading = (
		<div className="space-y-3">
			{[1, 2, 3].map((index) => (
				<div key={index} className={`p-4 rounded-lg border ${isLight ? 'bg-gray-50 border-gray-200' : 'bg-[#263340] border-[#374151]'}`}>
					<div className="animate-pulse">
						<div className="flex items-center justify-between mb-3">
							<div className="flex items-center space-x-3">
								<div className={`w-10 h-10 rounded-full ${isLight ? 'bg-gray-200' : 'bg-gray-600'}`}></div>
								<div className="space-y-2">
									<div className={`h-4 rounded w-32 ${isLight ? 'bg-gray-200' : 'bg-gray-600'}`}></div>
									<div className={`h-3 rounded w-24 ${isLight ? 'bg-gray-200' : 'bg-gray-600'}`}></div>
								</div>
							</div>
							<div className="flex space-x-2">
								<div className={`h-8 rounded w-20 ${isLight ? 'bg-gray-200' : 'bg-gray-600'}`}></div>
								<div className={`h-8 rounded w-24 ${isLight ? 'bg-gray-200' : 'bg-gray-600'}`}></div>
							</div>
						</div>
						<div className="flex items-center space-x-4">
							<div className={`h-3 rounded w-28 ${isLight ? 'bg-gray-200' : 'bg-gray-600'}`}></div>
							<div className={`h-3 rounded w-28 ${isLight ? 'bg-gray-200' : 'bg-gray-600'}`}></div>
						</div>
					</div>
				</div>
			))}
		</div>
	)

	const accountsTab = (
		<>
			{(error || warning) && (
				<div className={`px-4 py-3 rounded-lg mb-4 flex items-center justify-between ${
					error
						? `bg-red-500/10 border border-red-500/50 ${isLight ? 'text-red-600' : 'text-red-400'}`
						: `bg-amber-500/10 border border-amber-500/50 ${isLight ? 'text-amber-800' : 'text-amber-400'}`
				}`}>
					<span>{error || warning}</span>
					<button
						onClick={() => {
							setError(null)
							setWarning(null)
						}}
						className="opacity-80 hover:opacity-100"
					>
						<svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
							<path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
						</svg>
					</button>
				</div>
			)}
			{loading ? accountsLoading : loadFailed ? null : accounts.length === 0 ? (
				<div className={`p-4 rounded-lg border text-center ${
					isLight
						? 'bg-gray-50 border-gray-200'
						: 'bg-[#263340] border-[#374151]'
				}`}>
					<p className={isLight ? 'text-gray-500' : 'text-gray-400'}>No email accounts connected yet.</p>
				</div>
			) : (
				<EmailAccountList
					accounts={accounts}
					onReAuthorization={handleReAuthorization}
					canEnableRead={Boolean(getCurrentUser()?.isAdmin)}
					onDeleteAccount={handleDeleteAccount}
					onSaveSignature={handleSaveSignature}
					onRemoveSignature={handleRemoveSignature}
				/>
			)}
		</>
	)

	return (
		<>
			<SuccessMessageHandler show={showSuccess} onClose={hideSuccess} />
			<div className={isLight ? 'text-gray-900' : 'text-white'}>
				<section className="mb-8">
					<div className="flex flex-col gap-3 mb-4 sm:flex-row sm:items-center sm:justify-between">
						<div
							role="tablist"
							className={`flex w-full sm:inline-flex sm:w-auto gap-0.5 p-0.5 rounded-md ${isLight ? 'bg-gray-100' : 'bg-[#15202b]'}`}
						>
							{TABS.map(tab => (
								<button
									key={tab.id}
									type="button"
									role="tab"
									aria-selected={activeTab === tab.id}
									onClick={() => switchTab(tab.id)}
									className={`flex-1 sm:flex-none px-3 py-1 rounded text-sm font-medium transition-colors duration-150 ${
										activeTab === tab.id
											? isLight ? 'bg-white text-gray-900 shadow-sm' : 'bg-[#263340] text-white'
											: isLight ? 'text-gray-500 hover:text-gray-700' : 'text-gray-400 hover:text-gray-200'
									}`}
								>
									{tab.label}
								</button>
							))}
						</div>
						{activeTab === 'templates' ? (
							<button type="button" onClick={() => templatesRef.current?.startNewTemplate()} className={ACTION_BUTTON}>
								+ New template
							</button>
						) : (
							<button type="button" onClick={handleModalOpen} disabled={loading} className={ACTION_BUTTON}>
								+ Connect New Account
							</button>
						)}
					</div>
					{activeTab === 'templates' ? <EmailTemplates ref={templatesRef} /> : accountsTab}
				</section>
			</div>

			<ConnectEmailAccountModal
				isOpen={isConnectModalOpen}
				onClose={() => setIsConnectModalOpen(false)}
				onAuthorize={handleEmailAuthorization}
				error={modalError}
				submitting={submitting}
				allowOutlook={Boolean(getCurrentUser()?.isAdmin)}
				allowReadAccess={Boolean(getCurrentUser()?.isAdmin)}
			/>

			{isMaxAccountsModalOpen && (
				<LimitReachedModal
					title="Maximum Accounts Reached"
					message={mailboxLimitMessage(mailboxAllowance)}
					onClose={() => setIsMaxAccountsModalOpen(false)}
				/>
			)}
		</>
	)
}

export default EmailSettings
